<template>
	<div class="d-inline-flex cursor-pointer h-100">
		<div v-for="item in localData" @click="selectPeriod(item)" class="bg-white p-3 month-period mx-2" :class="{ 'borderPrimary-light': item.selected, 'disabled': !item.canUse }" style="">
			<div class="d-flex justify-content-between">
				<div class="d-flex">
					<small-checkbox :checked="item.selected" @click.prevent="selectPeriod(item)"></small-checkbox>
					<label class="text-bold">{{ item.displayName }}</label>
				</div>
				<div v-if="!item.isMain" style="width: 35px;height: 15px;border-radius: 3px" class="bg-primary-light small px-1">{{ $t('salary.virtual') }}</div>
			</div>
			<div class="d-flex justify-content-between hover-div">
				<small-checkbox :checked="item.isLocked" icon="lock" @update:modelValue="(status) => lockButton.handler(status, item)" :options="lockButton.options" class="" style="border-radius: 100%;border-width: thin" />

				<div v-if="!isModal" tabindex="0" class="text-left d-none group-buttons">
					<nk-button v-for="button in buttons" :class="button.class" @click="() => button.handler(item)" @submit="button.handler" :options="button.options" class="" />
				</div>
			</div>
		</div>
	</div>
</template>

<script lang="ts">
	import smallCheckbox from '@/components/general/small-checkbox.vue'
	import { defineAsyncComponent, defineComponent } from 'vue'
	import dialog from '@/plugins/nikan-dialog-plugin'
	import $sc from '@/plugins/shortcut.collection'
	import { MonthPeriodCardOptions, SalaryPeriod, SubPeriod } from '@/classes/salary/salary-period'
	import { post } from '@/plugins/nikan-api'
	import Endpoints from '@nikan/endpoints'
	import { TYPE } from 'vue-toastification'
	import { mapActions, mapGetters } from 'vuex'
	import merge from 'lodash/merge'
	import copyPeriod from '@/views/salary/salary-period/copy-period.vue'

	export default defineComponent({
		name: "month-period-card",
		components: { smallCheckbox },
		emits: ['update:modelValue', 'resetPeriods'],
		props: {
			options: {
				type: Object
			},
			isModal: {
				type: Boolean,
				default: false,
			},
			data: Array,
		},
		created() {
			merge(this.settings, this.options)
		},
		data() {
			return {
				settings: new MonthPeriodCardOptions(),
				localData: [] as any,
				state: false,
				lockButton: {
					handler: (status: boolean, period: SubPeriod) => {
						this.lockPeriod(status, period)
					},
					class: " borderPrimary-light thin ",
					type: 'nk-btn',
					options: {
						visible: true,
						isPrimary: false,
						icon: 'lock',
						tooltipEnabled: true,
						hideTitle: false,
						title: '',
						policyClaims: 'Salary_SalaryPeriod_Lock'
					}
				},
				buttons: [
					{
						handler: this.handlerBtnCopy,
						class: "no-outline text-dark",
						type: 'nk-btn',
						options: {
							visible: true,
							isPrimary: false,
							icon: 'copy',
							tooltipEnabled: true,
							hideTitle: true,
							title: this.$t('PrivilegeTypes.Copy'),
							policyClaims: 'Salary_SalaryPeriod_Copy'
						}
					},
					{
						handler: this.handlerBtnEdit,
						class: "no-outline text-dark",
						type: 'nk-btn',
						options: {
							visible: true,
							isPrimary: false,
							icon: 'edit',
							tooltipEnabled: true,
							hideTitle: true,
							title: this.$t('PrivilegeTypes.Edit'),
							policyClaims: 'Salary_SalaryPeriod_Edit'
						}
					},
					{
						handler: this.handlerBtnAccessClaim,
						class: "no-outline text-dark",
						type: 'nk-btn',
						options: {
							visible: true,
							isPrimary: false,
							icon: 'user-lock',
							tooltipEnabled: true,
							hideTitle: true,
							policyClaims: 'General_Access_Management',
							title: this.$t('operationRole.claim'),
						}
					},
					{
						handler: this.handlerBtnDelete,
						class: "no-outline btn-outline-danger",
						type: 'nk-btn',
						options: {
							visible: true,
							isPrimary: false,
							icon: 'trash',
							tooltipEnabled: true,
							hideTitle: true,
							title: this.$t('PrivilegeTypes.Delete'),
							policyClaims: 'Salary_SalaryPeriod_Delete'
						}
					},
				]
			}
		},
		watch: {
			data: {
				immediate: true,
				deep: true,
				handler(val) {
					this.localData = val
				}
			}
		},
		computed: {
			...mapGetters({
				getSalaryPeriod: 'appContext/getSalaryPeriod',
			}),
		},
		methods: {
			...mapActions({
				refreshSalaryPeriod: 'salaryPeriods/refresh'
			}),
			handlerBtnDelete(data: SubPeriod) {
				if (data.isMain && this.settings.period.subPeriods.some((x: any) => !x.isMain)) {
					this.$toast(this.$t('salary.period.until-any-virtual-period-exist-cant-delete'))
				} else if (this.getSalaryPeriod.id === data.id) {
					this.$toast(this.$t('salary.period.could-not-delete-current-salary-period'))
				} else {
					dialog({
						title: this.$t('salary.delete-title'),
						body: this.$t('salary.delete-body', { periodName: `${data.displayName} (${this.settings?.period.pYear + ' ' + this.settings?.period.pMonthName}) ` }),
						autoClose: true,
						buttons: [
							{
								title: this.$t('dialog.yes'),
								shortcut: $sc.find('yes', 'dialog'),
								isPrimary: true,
								handle: () => {
									this.deletePeriod(data)
								},
							},
							{
								title: this.$t('dialog.cancel'),
								shortcut: $sc.find('cancel', 'dialog'),
								isCancel: true,
								handle: () => {
								},
							},
						],
					})
				}
			},
			handlerBtnEdit(data: SubPeriod) {
				this.settings?.insertPeriodFunc(data)
			},
			handlerBtnCopy(data: SubPeriod) {
				this.showModal({
					component: copyPeriod,
					options: {
						selectedSourcePeriod: data
					}
				}).catch(() => { })
			},
			handlerBtnAccessClaim(data: SubPeriod) {
				this.showModal({
					component: defineAsyncComponent(() => import('@/views/operation-roles/access-control.vue')),
					data: {
						prefix: 'Salary',
						accessData: data
							? {
								entityId: data.id,
								entityType: 'SalaryPeriod',
								objectDescription: `${this.$t('salary.salary-period')} ${this.settings?.period.pYear} ${this.settings?.period.pMonthName}  `,
							}
							: null,
					},
				}).then((res) => { })
					.catch(() => { })
			},
			selectPeriod(period: SubPeriod) {
				if (!period.canUse) {
					this.$toast(this.$t('salary.period.could-not-select-restricted-period'), { type: TYPE.ERROR })
					return
				}
				this.$emit('update:modelValue', period)
			},
			lockPeriod(status: boolean, period: SubPeriod) {
				//@ts-ignore
				var wait = this.$wait(this.grandParent)
				const payLoad = {
					"salaryPeriodId": period.id,
					"lock": status,
				}
				post(Endpoints.getUrl('salaryPeriod.lock'), payLoad, null, null)
					.then((result: any) => {
						this.$toast(this.$t(`salary.period-action-successfully`, {
							periodName: this.settings?.period.pYear + ' ' + this.settings?.period.pMonthName + ' ' + period.displayName,
							state: status ? this.$t('salary.lock') : this.$t('salary.unlock')
						}), { type: TYPE.SUCCESS })
						this.refreshSalaryPeriod()
						// this.$emit('resetPeriods')
						wait.end()
					})
					.catch(() => {
						wait.end()
					})
			},
			deletePeriod(period: SubPeriod) {
				//@ts-ignore
				var wait = this.$wait(this.grandParent)
				const payLoad = {
					"salaryPeriodId": period.id,
				}
				post(Endpoints.getUrl('salaryPeriod.delete'), payLoad)
					.then(async (result: any) => {
						await this.refreshSalaryPeriod()
						this.$toast(this.$t('msg.delete-success'), { type: TYPE.SUCCESS })
						this.$emit('resetPeriods')
						wait.end()
					})
					.catch(() => {
						wait.end()
					})
			}
		}
	})
</script>

<style scoped>
	.month-period {
		height: 70px;
		width: 250px;
		border: lightgray solid thin;
		border-radius: 6px
	}

	.month-period:hover>.hover-div>.group-buttons {
		display: block !important;
	}

	.month-period.disabled label {
		color: #aaa;
	}
</style>